import { type ReactNode } from "react";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/* DataTable — guide §5: header is overline slate on slate-100, no vertical
   borders, hover row uses surface-alt, numeric columns right-aligned with
   tabular nums. Generic over the row type. */

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Right-align + tabular nums (use for money, hours, counts). */
  numeric?: boolean;
  /** Show this column in the sticky-right "Actions" slot. */
  actions?: boolean;
  /** Width hint (CSS string), or "min" to shrink-to-content. */
  width?: string;
  className?: string;
  /** Sort key — when set, header becomes interactive. */
  sortKey?: string;
  cell: (row: T) => ReactNode;
}

export interface DataTableSort {
  key: string;
  direction: "asc" | "desc";
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Stable row id; falls back to index. */
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  /** When loading=true, render N skeleton rows. */
  skeletonRows?: number;
  emptyState?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Double-click a row to open it (detail page / primary action).
   *  Ignored when the double-click lands on an interactive element
   *  (button, link, input, kebab menu) so row actions still work. */
  onRowDoubleClick?: (row: T) => void;
  sort?: DataTableSort;
  onSortChange?: (next: DataTableSort) => void;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  skeletonRows = 5,
  emptyState,
  onRowClick,
  onRowDoubleClick,
  sort,
  onSortChange,
  className,
}: DataTableProps<T>) {
  const isEmpty = !loading && rows.length === 0;

  function handleHeaderClick(col: DataTableColumn<T>) {
    if (!col.sortKey || !onSortChange) return;
    const same = sort?.key === col.sortKey;
    onSortChange({
      key: col.sortKey,
      direction: same && sort?.direction === "asc" ? "desc" : "asc",
    });
  }

  return (
    <div className={cn("overflow-x-auto rounded-card border border-slate-200 bg-white", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-soft text-left">
            {columns.map((col) => {
              const sorted = sort?.key === col.sortKey;
              const sortable = !!col.sortKey && !!onSortChange;
              return (
                <th
                  key={col.key}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  className={cn(
                    "px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate",
                    col.numeric && "text-right",
                    col.actions && "text-right",
                    col.className,
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => handleHeaderClick(col)}
                      className={cn(
                        // UA stylesheets reset text-transform on <button>,
                        // which made sortable headers Title Case while plain
                        // ones stayed uppercase — restate the th's overline
                        // styling so every header matches.
                        "inline-flex items-center gap-1 uppercase tracking-wider hover:text-navy",
                        col.numeric && "ml-auto",
                      )}
                    >
                      {col.header}
                      {sorted ? (
                        sort!.direction === "asc" ? (
                          <ChevronUp size={12} aria-hidden="true" />
                        ) : (
                          <ChevronDown size={12} aria-hidden="true" />
                        )
                      ) : (
                        <ChevronsUpDown size={12} aria-hidden="true" className="opacity-50" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {loading &&
            Array.from({ length: skeletonRows }).map((_, idx) => (
              <tr key={`sk-${idx}`} className="border-t border-slate-200">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3">
                    <Skeleton className="h-4 w-full max-w-[180px]" />
                  </td>
                ))}
              </tr>
            ))}

          {!loading &&
            rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onDoubleClick={
                  onRowDoubleClick
                    ? (e) => {
                        // Don't hijack a double-click that lands on a
                        // button/link/field/kebab — those have their own
                        // behavior; only bare-cell double-clicks open the row.
                        if (
                          (e.target as HTMLElement).closest(
                            "button, a, input, select, textarea, label, [role='menu']",
                          )
                        ) {
                          return;
                        }
                        onRowDoubleClick(row);
                      }
                    : undefined
                }
                className={cn(
                  "border-t border-slate-200 transition-colors",
                  onRowClick && "cursor-pointer hover:bg-bg",
                  onRowDoubleClick && "select-none hover:bg-bg",
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "px-4 py-3 align-middle text-foreground",
                      col.numeric && "text-right tabular-nums",
                      col.actions && "text-right whitespace-nowrap",
                      col.className,
                    )}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))}

          {isEmpty && emptyState && (
            <tr>
              <td colSpan={columns.length} className="p-0">
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
